import React, { useEffect, useState } from 'react';
import { useSpring } from '@react-spring/web'

export const useMediaQuery = query => {
	const [isMatched, setIsMatched] = useState(false);

	const handleResize = () => {
		setIsMatched(window.matchMedia(query).matches);
	};

	useEffect(() => {
		setIsMatched(window.matchMedia(query).matches);
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [query]);

	return isMatched;
}

export const useScrollCounter2 = (initialValue, minValue, maxValue, delay) => {
	const viewport = window;
	const [touchStartPositionY, setTouchStartPositionY] = useState(0);
	const [currentValue, setCurrentValue] = useState(initialValue);
    const [isAnimationPlaying, setIsAnimationPlaying] = useState(false);
    const [direction, setDirection] = useState(0);

	const updateValue = (scrollDiff) => {
		const isMovingUp = scrollDiff < 0;
		const isMovingDown = scrollDiff > 0;
		const isFirstAnimationIndex = currentValue <= minValue;
		const isLastAnimationIndex = currentValue >= maxValue;

		if ((isMovingUp && isFirstAnimationIndex) || (isMovingDown && isLastAnimationIndex)) {
			return;
		}
		if (isMovingUp) {
			setCurrentValue(currentValue - 1)
		}
		if (isMovingDown) {
			setCurrentValue(currentValue + 1)
		}
        setDirection(Math.sign(scrollDiff));
	};

	const handleScrollChange = (e) => {
        if (isAnimationPlaying) {
            return ;
        }

		setIsAnimationPlaying(true);
        updateValue(e.deltaY);
        setTimeout(() => {
            setIsAnimationPlaying(false);
        }, delay);
	};

	const handleTouchStart = (e) => {
		setTouchStartPositionY(e.touches[0].pageY);
	};

	const handleTouchEnd = (e) => {
		const deltaY = touchStartPositionY - e.changedTouches[0].pageY;
		const threshold = 10;

		if (Math.abs(deltaY) > threshold)
		handleScrollChange({deltaY});
	};

	useEffect(() => {
		viewport.addEventListener('wheel', handleScrollChange);
		viewport.addEventListener('touchstart', handleTouchStart);
		viewport.addEventListener('touchend', handleTouchEnd);

		return () => {
			viewport.removeEventListener('wheel', handleScrollChange);
			viewport.removeEventListener('touchstart', handleTouchStart);
			viewport.removeEventListener('touchend', handleTouchEnd);
		};
	}, [touchStartPositionY, isAnimationPlaying, currentValue]);

    return [currentValue, setCurrentValue];
};

export const useScrollCounter = (initialValue, minValue, maxValue, delay) => {
	const viewport = document.body;
    const [timer, setTimer] = useState(null);
	const [currentValue, setCurrentValue] = useState(initialValue);
	const [initialScrollPosition, setInitialScrollPosition] = useState(viewport.clientHeight / 2);
    const [isScrollToCalled, setIsScrollToCalled] = useState(false);
    const [direction, setDirection] = useState(0);

	const updateValue = (newScrollPosition) => {
		const scrollDiff = newScrollPosition - initialScrollPosition;
		const isMovingUp = scrollDiff < 0;
		const isMovingDown = scrollDiff > 0;
		const isFirstAnimationIndex = currentValue <= minValue;
		const isLastAnimationIndex = currentValue >= maxValue;

		if ((isMovingUp && isFirstAnimationIndex) || (isMovingDown && isLastAnimationIndex)) {
			return;
		}
		if (isMovingUp) {
			setCurrentValue(currentValue - 1)
		}
		if (isMovingDown) {
			setCurrentValue(currentValue + 1)
		}
        setDirection(Math.sign(scrollDiff));
	};

	const handleScrollChange = (e) => {
        if (isScrollToCalled) {
            return setIsScrollToCalled(false);
        }

		if (timer) {
			return;
		}

        updateValue(viewport.scrollY);
        // viewport.scrollTo(0, initialScrollPosition);
        const newTimer = setTimeout(() => {
            viewport.scrollTo(0, initialScrollPosition);
            setIsScrollToCalled(true);
            setTimer(null);
        }, delay);
		setTimer(newTimer);
	};

	const handleResize = () => {
		const newInitialScrollPosition = viewport.clientHeight / 2;
		if (initialScrollPosition !== newInitialScrollPosition) {
			setInitialScrollPosition(newInitialScrollPosition);
		}
	};

	useEffect(() => {
		viewport.addEventListener('scroll', handleScrollChange);
		viewport.addEventListener('resize', handleResize);

		return () => {
			viewport.removeEventListener('scroll', handleScrollChange);
			viewport.removeEventListener('resize', handleResize);
		};
	}, [initialScrollPosition, isScrollToCalled, timer]);

    return [currentValue, direction];
};

export const useAnimation = (currentStep, animation, config) => {
	let style = animation.default;
	const stepIndexes = Object.keys(animation.steps).reverse();

	for (const step of stepIndexes) {
		if (currentStep >= step) {
			style = animation.steps[step];
			break;
		}
	}
	return style;
	return config
		? useSpring({
			...style,
			config
		})
		: style;
}

export const useSensors = () => {
	const [orientation, setOrientation] = useState({
		a: 0,
		b: 0,
		g: 0,
	});
	const handleOrientation = (event) => {
		setOrientation({
			a: Math.round(event.alpha),
			b: Math.round(event.beta),
			g: Math.round(event.gamma),
		});
	}

	useEffect(() => {
		window.addEventListener("deviceorientation", handleOrientation);
		return () => {
			window.removeEventListener("deviceorientation", handleOrientation);
		};
	}, []);

	if (orientation.b > 0 && orientation.b < 85) {
		return Math.round(orientation.g / 4);
	}

	return 0;
}

export const usePointer = () => {
	const [position, setPosition] = useState({
		x: 0,
		y: 0,
	});

	const handleMousemove = (event) => {
		setPosition({
			x: event.pageX,
			y: event.pageY
		});
	}

	useEffect(() => {
		window.addEventListener("mousemove", handleMousemove);
		return () => {
			window.removeEventListener("mousemove", handleMousemove);
		};
	}, []);

	return position;
}
